/* The Good Plant Co — shared behaviour: reveal-on-scroll, AJAX cart, toast, googly eyes. */
(() => {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Per-section init registry ----------
     The theme editor swaps a section's DOM on every setting change, so any
     init that binds to elements must be re-runnable against a scope. Section
     {% javascript %} blocks push onto window.tgpcInit too; everything runs
     once at load and again per section on shopify:section:load. */
  const inits = (window.tgpcInit = window.tgpcInit || []);
  const runInits = (scope) => inits.forEach((init) => init(scope));

  /* ---------- Stefan doodles ----------
     Emoji typed in the editor carry an invisible variation selector (U+FE0F)
     that forces the colour emoji font; strip it inside hand-font elements so
     the Stefan doodle glyphs render instead. */
  inits.push((scope) => {
    scope
      .querySelectorAll('.hand, .eyebrow, .ticker__group span, .hero__stamp-wrap, .footer__liner, .value-props__glyph, .chip, .badge')
      .forEach((el) => {
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          if (node.nodeValue.includes('\uFE0F')) node.nodeValue = node.nodeValue.replaceAll('\uFE0F', '');
        }
      });
  });

  /* ---------- Reveal on scroll ---------- */
  const io =
    !reducedMotion && 'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('in');
                io.unobserve(entry.target);
              }
            });
          },
          { threshold: 0.12, rootMargin: '0px 0px -4% 0px' }
        )
      : null;
  inits.push((scope) => {
    scope.querySelectorAll('.rv').forEach((el) => (io ? io.observe(el) : el.classList.add('in')));
  });

  if (document.readyState === 'complete') runInits(document);
  else document.addEventListener('DOMContentLoaded', () => runInits(document));
  document.addEventListener('shopify:section:load', (event) => runInits(event.target));

  /* ---------- Shared helpers ---------- */
  if (window.tgpc) {
    /* Fetch a Section Rendering API URL and hand back one node out of it */
    window.tgpc.fetchSection = (url, selector, opts) =>
      fetch(url, opts)
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((html) => new DOMParser().parseFromString(html, 'text/html').querySelector(selector));

    /* Re-run every registered init against a freshly swapped DOM scope
       (AJAX section swaps outside the theme editor need this). */
    window.tgpc.runInits = runInits;
  }

  /* ---------- Dialogs (menu drawer, search overlay, exit nudge) ----------
     [data-dialog-open="<selector>"] opens a dialog, [data-dialog-close]
     closes its own, and a click that lands on a [data-backdrop-close]
     dialog itself (only possible on the backdrop) closes it. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-dialog-open]');
    if (opener) {
      const dialog = document.querySelector(opener.dataset.dialogOpen);
      if (dialog) {
        dialog.showModal();
        dialog.querySelector('[autofocus]')?.focus();
      }
      return;
    }
    if (event.target.closest('[data-dialog-close]')) {
      event.target.closest('dialog')?.close();
      return;
    }
    if (event.target.matches?.('dialog[open][data-backdrop-close]')) event.target.close();
  });

  /* ---------- Quantity steppers ----------
     [data-step="±1"] steps the sibling number input, clamped to its own min;
     the bubbled change event drives whatever the host section does next
     (PDP reads it at submit, cart page debounce-submits, drawer syncs). */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-step]');
    if (!button) return;
    const input = button.parentElement.querySelector('input[type="number"]');
    if (!input) return;
    const min = input.min === '' ? 0 : Number(input.min);
    input.value = Math.max(min, (Number(input.value) || 0) + Number(button.dataset.step));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* ---------- Carousels (native scroll-snap + arrows) ----------
     Markup: [data-carousel] wraps a .carousel track; [data-carousel-prev] and
     [data-carousel-next] buttons scroll it one card at a time. Touch/trackpad
     swiping is native; this only powers the arrows + their disabled states. */
  inits.push((scope) => {
    scope.querySelectorAll('[data-carousel]').forEach((root) => {
      const track = root.querySelector('.carousel');
      if (!track || track.tgpcCarousel) return;
      track.tgpcCarousel = true;
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      const step = () => {
        const card = track.firstElementChild;
        return card ? card.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0) : track.clientWidth;
      };
      const sync = () => {
        if (prev) prev.disabled = track.scrollLeft < 4;
        if (next) next.disabled = track.scrollLeft > track.scrollWidth - track.clientWidth - 4;
      };
      const scrollBehavior = reducedMotion ? 'auto' : 'smooth';
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: scrollBehavior }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: scrollBehavior }));
      track.addEventListener('scroll', sync, { passive: true });
      sync();
    });
  });

  /* ---------- Toast ---------- */
  const toastEl = document.querySelector('[data-toast]');
  let toastTimer;
  window.tgpcToast = (message) => {
    if (!toastEl || !message) return;
    toastEl.textContent = message;
    toastEl.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('show'), 3200);
  };

  /* ---------- Cart count ---------- */
  document.addEventListener('cart:updated', (event) => {
    const count = event.detail && event.detail.item_count;
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.closest('[data-cart-button]')?.classList.add('pop');
      setTimeout(() => el.closest('[data-cart-button]')?.classList.remove('pop'), 350);
    });
  });

  /* ---------- AJAX add to cart ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-ajax-add]');
    if (!form || !window.tgpc) return;
    event.preventDefault();

    const submitButton = form.querySelector('button[type="submit"], input[type="submit"]');
    if (submitButton) {
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.disabled = true;
    }

    /* clap-cursor celebration, instantly on click (see --cursor-clap) */
    const root = document.documentElement;
    root.classList.add('cursor-clap');
    clearTimeout(root.clapTimer);
    root.clapTimer = setTimeout(() => root.classList.remove('cursor-clap'), 1400);

    try {
      /* Bundled section rendering: every mutation asks for the drawer's HTML
         in the same response, so no follow-up /cart.js or section fetch. */
      const body = new FormData(form);
      body.append('sections', 'cart-drawer');

      const response = await fetch(`${window.tgpc.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        window.tgpcToast(error.description || error.message || window.tgpc.strings.cartError);
        return;
      }

      let result = await response.json();

      /* ticked add-ons ride along as their own line items — one bundled call */
      const addons = Array.from(form.querySelectorAll('[data-addon]:checked'));
      if (addons.length) {
        const addonResponse = await fetch(`${window.tgpc.routes.cartAdd}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: addons.map((addon) => ({ id: Number(addon.value), quantity: 1 })),
            sections: 'cart-drawer',
          }),
        }).catch(() => null);
        if (addonResponse?.ok) {
          const addonResult = await addonResponse.json().catch(() => null);
          if (addonResult) {
            result = addonResult;
            addons.forEach((addon) => (addon.checked = false));
          } else {
            window.tgpcToast(window.tgpc.strings.cartError);
          }
        } else {
          const addonError = addonResponse ? await addonResponse.json().catch(() => ({})) : {};
          window.tgpcToast(addonError.description || addonError.message || window.tgpc.strings.cartError);
        }
      }

      /* add.js responses carry no totals — read the count out of the bundled drawer HTML */
      const drawerHtml = result.sections && result.sections['cart-drawer'];
      const detail = { sections: result.sections };
      if (drawerHtml) {
        const count = Number(
          new DOMParser().parseFromString(drawerHtml, 'text/html').querySelector('.cart-drawer__count')?.textContent
        );
        if (!Number.isNaN(count)) detail.item_count = count;
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail }));
      /* the cart drawer opening is the feedback; toast only without one */
      if (!document.querySelector('[data-cart-drawer]')) window.tgpcToast(window.tgpc.strings.added);
    } catch {
      form.submit();
    } finally {
      if (submitButton) {
        submitButton.removeAttribute('aria-busy');
        submitButton.disabled = false;
      }
    }
  });

  /* ---------- Exit nudge: don't leaf us like this ---------- */
  const exitDialog = document.querySelector('[data-exit-intent]');
  if (exitDialog && !window.Shopify?.designMode) {
    const cartCount = () => parseInt(document.querySelector('[data-cart-count]')?.textContent || '0', 10);
    let exitShown = false;

    document.addEventListener('mouseout', (event) => {
      if (exitShown) return;
      if (event.relatedTarget || event.clientY > 24) return;
      if (cartCount() < 1) return;
      if (document.querySelector('dialog[open]')) return;
      exitShown = true;
      exitDialog.showModal();
    });
  }

  /* ---------- Googly eyes follow the cursor ---------- */
  if (!reducedMotion && window.matchMedia('(pointer: fine)').matches) {
    /* Cache registered and visible pupils so cursor frames never scan the DOM.
       A mutation observer keeps section/cart re-renders in the cache. */
    const pupils = new Set();
    const visiblePupils = new Set();
    const visibility = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.target.isConnected) visiblePupils.add(entry.target);
        else visiblePupils.delete(entry.target);
      });
    });
    const registerPupils = (scope) => {
      const found = [];
      if (scope.matches?.('.pupil')) found.push(scope);
      scope.querySelectorAll?.('.pupil').forEach((pupil) => found.push(pupil));
      found.forEach((pupil) => {
        if (pupils.has(pupil)) return;
        pupils.add(pupil);
        visibility.observe(pupil);
      });
    };
    const unregisterPupils = (scope) => {
      if (scope.isConnected) return;
      const found = [];
      if (scope.matches?.('.pupil')) found.push(scope);
      scope.querySelectorAll?.('.pupil').forEach((pupil) => found.push(pupil));
      found.forEach((pupil) => {
        pupils.delete(pupil);
        visiblePupils.delete(pupil);
        visibility.unobserve(pupil);
      });
    };
    registerPupils(document);
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) registerPupils(node);
        });
        record.removedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) unregisterPupils(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });

    let raf = null;
    let pointerX = 0;
    let pointerY = 0;
    document.addEventListener('mousemove', (event) => {
      pointerX = event.clientX;
      pointerY = event.clientY;
      if (!visiblePupils.size) return;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        visiblePupils.forEach((pupil) => {
          const white = pupil.previousElementSibling; /* <g class="eye"><circle #fff/><circle .pupil/></g> */
          if (!white || !white.r) return;
          /* Aim from the eye-white's centre (stable — the pupil itself moves).
             Everything below is in SVG user units: a px translate on an SVG
             element IS user units, so this stays correct at every render size. */
          const box = white.getBoundingClientRect();
          const scale = box.width / (white.r.baseVal.value * 2) || 1; /* screen px per user unit */
          const dx = pointerX - (box.left + box.width / 2);
          const dy = pointerY - (box.top + box.height / 2);
          const dist = Math.hypot(dx, dy) || 1;
          const travel = (white.r.baseVal.value - pupil.r.baseVal.value) * 0.92; /* full socket, hair of rim inset */
          const deflect = Math.min(dist / scale, travel); /* cursor over the eye → pupil sits under it */
          const tx = (dx / dist) * deflect - (pupil.cx.baseVal.value - white.cx.baseVal.value);
          const ty = (dy / dist) * deflect - (pupil.cy.baseVal.value - white.cy.baseVal.value);
          pupil.style.transform = `translate(${tx}px, ${ty}px)`;
        });
      });
    });
    /* cursor gone → settle back into the resting googly pose */
    document.documentElement.addEventListener('mouseleave', () => {
      pupils.forEach((pupil) => (pupil.style.transform = ''));
    });
  }
})();
